import { type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowLeft, IndianRupee, Lock } from 'lucide-react';
import { PAYABLE_STAGES } from '@tailoring-crm/shared';
import { RateMatrix } from '../components/staff/rate-matrix.js';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Button, PageSkeleton, useToast } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useDefaultRates, useSetDefaultRate } from '../lib/staff/use-staff.js';

/**
 * The shop's own rate card — the floor everybody falls back to.
 *
 * This screen exists so a shop is not forced to fill a nine-by-five matrix per person before it
 * can book any work. Set what a blouse pays here once, and every karigar resolves to it until
 * somebody is worth more, at which point their own page overrides just the cells that differ.
 *
 * It lives under Settings rather than under Staff because it is a fact about the business, not
 * about a person — and it is behind the same permission for the same reason: a tailor may see
 * what THEY are paid, never what the shop pays everyone.
 */
export function RateCardPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { role } = useAuth();

  const canManage = role === 'owner' || role === 'manager';
  const rates = useDefaultRates({}, { enabled: canManage });
  const setRate = useSetDefaultRate();

  if (!canManage) {
    return (
      <>
        <PageHeader title={t('staff.rates.defaultsTitle')} />
        <EmptyState
          icon={Lock}
          title={t('staff.rates.noAccessTitle')}
          description={t('staff.rates.noAccessHint')}
        />
      </>
    );
  }

  return (
    <>
      <div className="pb-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/settings">
            <ArrowLeft aria-hidden="true" />
            {t('settings.title')}
          </Link>
        </Button>
      </div>

      <PageHeader
        title={t('staff.rates.defaultsTitle')}
        description={t('staff.rates.defaultsDescription')}
      />

      {rates.isPending ? (
        <PageSkeleton label={t('common.loading')} />
      ) : rates.data && rates.data.matrix.length > 0 ? (
        <div className="flex flex-col gap-4">
          <RateMatrix
            matrix={rates.data.matrix}
            stages={PAYABLE_STAGES}
            pending={setRate.isPending}
            onSetRate={({ garmentTypeKey, stage, ratePaise }) =>
              setRate.mutate(
                { garmentTypeKey, stage, ratePaise },
                {
                  onSuccess: () => toast({ title: t('staff.rates.saved') }),
                  onError: () => toast({ title: t('error.unexpected'), variant: 'destructive' }),
                },
              )
            }
          />
          {/* Saying this out loud, because it is the property that settles arguments. */}
          <p className="text-sm text-muted-foreground">{t('staff.rates.datedNote')}</p>
        </div>
      ) : (
        <EmptyState
          icon={IndianRupee}
          title={t('staff.rates.noGarmentsTitle')}
          description={t('staff.rates.noGarmentsHint')}
          action={
            <Button asChild variant="outline">
              <Link to="/settings/measurements">{t('settings.measurementsTitle')}</Link>
            </Button>
          }
        />
      )}
    </>
  );
}

export default RateCardPage;
